import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import type { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { Badge, DotSeparator } from '@seen/ui'
import { Fragment, type JSX } from 'react'
import { ClinicalHoverLayout } from '#src/components/clinical/ClinicalHoverLayout.tsx'
import { NoteBlock } from '#src/components/clinical/NoteBlock.tsx'
import { CONDITION_STATUS_COLORS } from '#src/components/clinical/statusConfigs.ts'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { SanitizedHTML } from '#src/components/core/SanitizedHTML.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export function ConditionCard({
  condition,
  to,
}: {
  condition: Diagnosis
  to?: string
}): JSX.Element {
  const { data: latestComment } = useSearchOne(
    CommentCommunication,
    CommentCommunication.searchQuery([condition.reference])
  )

  const sender = latestComment?.practitionerSender
  const senderRef = sender?.reference
    ? { reference: sender.reference, display: sender.display }
    : undefined

  return (
    <ClinicalHoverLayout
      to={to}
      title={
        <h3 className="text-[15px] leading-snug font-semibold">
          <SanitizedHTML htmlString={condition.title} inline />
        </h3>
      }
      status={
        <RowBadge
          icon={
            <LabelColor
              color={
                CONDITION_STATUS_COLORS[condition.clinicalStatus] ?? '#bec2c8'
              }
            />
          }
          className="pointer-events-none"
        >
          {condition.clinicalStatus}
        </RowBadge>
      }
      metadata={
        <>
          {condition.icd10Codes.map((code, i) => (
            <Fragment key={code}>
              {i > 0 && <DotSeparator />}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="code">{code}</Badge>
                </TooltipTrigger>
                <TooltipContent>ICD-10: {code}</TooltipContent>
              </Tooltip>
            </Fragment>
          ))}
          {condition.onsetDateString && (
            <>
              {condition.icd10Codes.length > 0 && <DotSeparator />}
              <HoverDate date={condition.onsetDateString} format="date" />
            </>
          )}
        </>
      }
      noteBlock={
        latestComment ? (
          <NoteBlock
            message={latestComment.message}
            senderRef={senderRef}
            sent={latestComment.sent}
            comment={latestComment}
          />
        ) : undefined
      }
    />
  )
}
